/**
 * schemas/session
 * The identity probe. Owned by skplatform-api, not by this product — SK
 * Company Cash has no auth UI at all, so this is the only platform contract
 * the app depends on.
 *
 * The response is envelope-wrapped (`{ data: { user, org, memberships } }`) on
 * the platform, so the schema describes the inner payload and the service
 * unwraps once.
 */
import { z } from 'zod';

export const sessionUserSchema = z.object({
  id: z.string().optional(),
  email: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  name: z.string().optional(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const sessionOrgSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  status: z.string().optional(),
});
export type SessionOrg = z.infer<typeof sessionOrgSchema>;

export const sessionPayloadSchema = z.object({
  user: sessionUserSchema.nullish(),
  org: sessionOrgSchema.nullish(),
  memberships: z.array(z.object({ role: z.string().optional() })).nullish(),
});
export type SessionPayload = z.infer<typeof sessionPayloadSchema>;

/** The display name for the header, from whichever fields the platform sent. */
export function displayName(user: SessionUser | null | undefined): string {
  if (!user) return '';
  const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return full || user.name || user.email || '';
}

/**
 * The header's org line: "Softknack Technologies · org_sk_001". Both halves
 * come from the platform; the id is shown because a person who administers two
 * orgs needs to know which one they are looking at.
 */
export function orgLine(org: SessionOrg | null | undefined): string {
  if (!org) return '';
  return [org.name, org.id].filter(Boolean).join(' · ');
}
